import { z } from 'zod';
import { RoleSchema } from './common';
import { BookingStatusSchema, CallModeSchema } from './booking';
import { CallStatusSchema } from './voice';
import { ProfessionalTypeSchema } from './matching';
import { TherapistProfileCompletenessSchema } from './therapist';

/**
 * Admin-only overview stats — GET /api/v1/admin/overview.
 *
 * Anonymity-first (ADR-003): this is aggregate counts plus a *minimal*
 * recent-bookings list, never names, emails, pseudonyms, or message
 * content. An admin dashboard is a real operational need, but it must not
 * become a backdoor around the anonymity guarantees the rest of the app
 * enforces.
 */

export const AdminUserStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  clients: z.number().int().nonnegative(),
  therapists: z.number().int().nonnegative(),
  admins: z.number().int().nonnegative(),
  newLast7Days: z.number().int().nonnegative(),
});
export type AdminUserStats = z.infer<typeof AdminUserStatsSchema>;

export const AdminBookingStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  byStatus: z.record(BookingStatusSchema, z.number().int().nonnegative()),
  anonymousCount: z.number().int().nonnegative(),
  standardCount: z.number().int().nonnegative(),
});
export type AdminBookingStats = z.infer<typeof AdminBookingStatsSchema>;

export const AdminCallStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  byStatus: z.record(CallStatusSchema, z.number().int().nonnegative()),
  avgDurationSeconds: z.number().nonnegative().nullable(),
});
export type AdminCallStats = z.infer<typeof AdminCallStatsSchema>;

export const AdminMessageStatsSchema = z.object({
  total: z.number().int().nonnegative(),
});
export type AdminMessageStats = z.infer<typeof AdminMessageStatsSchema>;

/** Sums are Decimal on the server — strings here, same as Booking money fields. */
export const AdminRevenueStatsSchema = z.object({
  currency: z.string().length(3),
  completedGrossTotal: z.string(),
  platformFeeTotal: z.string(),
});
export type AdminRevenueStats = z.infer<typeof AdminRevenueStatsSchema>;

/** Deliberately minimal — no client/therapist identity, no money, no notes. */
export const AdminRecentBookingSchema = z.object({
  id: z.string().cuid(),
  status: BookingStatusSchema,
  callMode: CallModeSchema,
  scheduledAt: z.string().datetime(),
});
export type AdminRecentBooking = z.infer<typeof AdminRecentBookingSchema>;

export const AdminOverviewResponseSchema = z.object({
  users: AdminUserStatsSchema,
  bookings: AdminBookingStatsSchema,
  calls: AdminCallStatsSchema,
  messages: AdminMessageStatsSchema,
  revenue: AdminRevenueStatsSchema,
  recentBookings: z.array(AdminRecentBookingSchema),
  generatedAt: z.string().datetime(),
});
export type AdminOverviewResponse = z.infer<typeof AdminOverviewResponseSchema>;

/**
 * Therapist vetting queue — GET/PATCH /api/v1/admin/therapists.
 *
 * Unlike the overview stats above, this deliberately DOES include therapist
 * identity (email, displayName, license). That's not a break of the
 * anonymity-first rule: therapists are never anonymous on this platform
 * (RegisterTherapistSchema requires a real displayName + license up front),
 * and an admin cannot vet a license without seeing who it belongs to. This
 * must never be reused as a pattern for exposing CLIENT identity.
 */

export const TherapistStatusSchema = z.enum(['PENDING_REVIEW', 'APPROVED', 'SUSPENDED', 'REJECTED']);
export type TherapistStatus = z.infer<typeof TherapistStatusSchema>;

export const AdminTherapistSummarySchema = z.object({
  id: z.string().cuid(), // TherapistProfile id
  userId: z.string().cuid(),
  email: z.string().email().nullable(),
  displayName: z.string().nullable(),
  status: TherapistStatusSchema,
  /**
   * Psychologist vs counsellor/coach. Set by the admin during vetting — the
   * same moment the licence is being checked — because the public page's claim
   * of "licensed psychologists only" rests entirely on this field.
   */
  professionalType: ProfessionalTypeSchema,
  licenseNumber: z.string().nullable(),
  licenseCountry: z.string().nullable(),
  specializations: z.array(z.string()),
  /** Bilingual profile text (ADR-015 §6) — Greek mandatory, English optional. See @ah/contracts therapist.ts. */
  headlineEl: z.string().nullable(),
  headlineEn: z.string().nullable(),
  bioEl: z.string().nullable(),
  bioEn: z.string().nullable(),
  photoMediaId: z.string().nullable(),
  photoUrl: z.string().nullable(),
  introVideoUrl: z.string().nullable(),
  caseTagsEl: z.array(z.string()),
  caseTagsEn: z.array(z.string()),
  /** ISO 639-1 codes — languages the therapist can hold a session in (distinct from the profile text's language). */
  languagesSpoken: z.array(z.string()),
  completeness: TherapistProfileCompletenessSchema,
  createdAt: z.string().datetime(),
  verifiedAt: z.string().datetime().nullable(),
});
export type AdminTherapistSummary = z.infer<typeof AdminTherapistSummarySchema>;

/** GET /api/v1/admin/therapists?status=PENDING_REVIEW */
export const AdminTherapistListQuerySchema = z.object({
  status: TherapistStatusSchema.optional(),
  professionalType: ProfessionalTypeSchema.optional(),
});
export type AdminTherapistListQuery = z.infer<typeof AdminTherapistListQuerySchema>;

export const AdminTherapistListResponseSchema = z.object({
  therapists: z.array(AdminTherapistSummarySchema),
});
export type AdminTherapistListResponse = z.infer<typeof AdminTherapistListResponseSchema>;

/** PATCH /api/v1/admin/therapists/:id/status */
export const UpdateTherapistStatusRequestSchema = z.object({
  status: TherapistStatusSchema,
  reason: z.string().max(500).optional(),
});
export type UpdateTherapistStatusRequest = z.infer<typeof UpdateTherapistStatusRequestSchema>;

export const UpdateTherapistStatusResponseSchema = z.object({
  therapist: AdminTherapistSummarySchema,
});
export type UpdateTherapistStatusResponse = z.infer<typeof UpdateTherapistStatusResponseSchema>;

/**
 * PATCH /api/v1/admin/therapists/:id/profile — admin curation of the
 * public-facing profile shown on the landing page (headline/bio/photo).
 * `photoMediaId` references an item uploaded via POST /admin/cms/media;
 * explicit null clears a field.
 */
export const UpdateTherapistProfileRequestSchema = z.object({
  professionalType: ProfessionalTypeSchema.optional(),
  headlineEl: z.string().min(1).max(200).nullable().optional(),
  headlineEn: z.string().min(1).max(200).nullable().optional(),
  bioEl: z.string().min(1).max(5000).nullable().optional(),
  bioEn: z.string().min(1).max(5000).nullable().optional(),
  photoMediaId: z.string().nullable().optional(),
  introVideoUrl: z.string().url().max(500).nullable().optional(),
  caseTagsEl: z.array(z.string().min(1).max(60)).max(20).optional(),
  caseTagsEn: z.array(z.string().min(1).max(60)).max(20).optional(),
  languagesSpoken: z.array(z.string().min(2).max(5)).max(10).optional(),
});
export type UpdateTherapistProfileRequest = z.infer<typeof UpdateTherapistProfileRequestSchema>;

export const UpdateTherapistProfileResponseSchema = z.object({
  therapist: AdminTherapistSummarySchema,
});
export type UpdateTherapistProfileResponse = z.infer<typeof UpdateTherapistProfileResponseSchema>;

/**
 * User administration — GET/PATCH /api/v1/admin/users.
 *
 * Anonymity-first (ADR-003/ADR-008): unlike the therapist summary above,
 * this covers CLIENT/ADMIN rows too, so it exposes only what the User model
 * already surfaces voluntarily (pseudonym, and email/displayName only if the
 * user upgraded) — never emergency contact, message content, or booking/call
 * history.
 */

export const AdminUserSummarySchema = z.object({
  id: z.string().cuid(),
  role: RoleSchema,
  email: z.string().email().nullable(),
  pseudonym: z.string().nullable(),
  displayName: z.string().nullable(),
  isAnonymous: z.boolean(),
  createdAt: z.string().datetime(),
  suspendedAt: z.string().datetime().nullable(),
  suspendedReason: z.string().nullable(),
  deletedAt: z.string().datetime().nullable(),
});
export type AdminUserSummary = z.infer<typeof AdminUserSummarySchema>;

/** GET /api/v1/admin/users?role=&q=&suspended= — `q` matches email/pseudonym/displayName. */
export const AdminUserListQuerySchema = z.object({
  role: RoleSchema.optional(),
  q: z.string().min(1).max(200).optional(),
  suspended: z.coerce.boolean().optional(),
  cursor: z.string().cuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AdminUserListQuery = z.infer<typeof AdminUserListQuerySchema>;

export const AdminUserListResponseSchema = z.object({
  users: z.array(AdminUserSummarySchema),
  hasMore: z.boolean(),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponseSchema>;

/** PATCH /api/v1/admin/users/:id/status */
export const UpdateUserStatusRequestSchema = z.object({
  suspended: z.boolean(),
  reason: z.string().max(500).optional(),
});
export type UpdateUserStatusRequest = z.infer<typeof UpdateUserStatusRequestSchema>;

export const UpdateUserStatusResponseSchema = z.object({
  user: AdminUserSummarySchema,
});
export type UpdateUserStatusResponse = z.infer<typeof UpdateUserStatusResponseSchema>;

/**
 * Review moderation — GET/PATCH /api/v1/admin/reviews.
 * `reviewerId` is intentionally omitted — an admin doesn't need the
 * reviewer's identity to judge whether a comment should stay visible.
 */

export const AdminReviewSummarySchema = z.object({
  id: z.string().cuid(),
  therapistProfileId: z.string().cuid(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().nullable(),
  isHidden: z.boolean(),
  hiddenAt: z.string().datetime().nullable(),
  hiddenReason: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type AdminReviewSummary = z.infer<typeof AdminReviewSummarySchema>;

/** GET /api/v1/admin/reviews?hidden=true|false (omitted = all) */
export const AdminReviewListQuerySchema = z.object({
  hidden: z.coerce.boolean().optional(),
});
export type AdminReviewListQuery = z.infer<typeof AdminReviewListQuerySchema>;

export const AdminReviewListResponseSchema = z.object({
  reviews: z.array(AdminReviewSummarySchema),
});
export type AdminReviewListResponse = z.infer<typeof AdminReviewListResponseSchema>;

/** PATCH /api/v1/admin/reviews/:id/moderate */
export const ModerateReviewRequestSchema = z.object({
  hidden: z.boolean(),
  reason: z.string().max(500).optional(),
});
export type ModerateReviewRequest = z.infer<typeof ModerateReviewRequestSchema>;

export const ModerateReviewResponseSchema = z.object({
  review: AdminReviewSummarySchema,
});
export type ModerateReviewResponse = z.infer<typeof ModerateReviewResponseSchema>;
